import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  FileText, 
  Upload, 
  Check, 
  X, 
  Clock, 
  RefreshCw, 
  Eye,
  History,
  AlertCircle,
  CheckCircle,
  Folder,
  Trash2,
  FileUp,
  Files,
  Loader2,
  Settings,
  Play,
  Pause,
  Timer,
  MonitorCheck,
  Server,
  HardDrive,
  ArrowLeft,
  Home,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportarAFDPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [arquivosImportados, setArquivosImportados] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [activeTab, setActiveTab] = useState('importar');
  
  // Upload de arquivos locais
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  
  // Configuração de pasta no servidor
  const [configuracao, setConfiguracao] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [pastaServidor, setPastaServidor] = useState('');
  const [arquivosServidor, setArquivosServidor] = useState([]);
  const [loadingArquivosServidor, setLoadingArquivosServidor] = useState(false);
  
  // Navegador de diretórios do servidor
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('');
  const [explorerItems, setExplorerItems] = useState([]);
  const [explorerParent, setExplorerParent] = useState('');
  const [loadingExplorer, setLoadingExplorer] = useState(false);
  
  // Monitoramento automático
  const [monitoramentoAtivo, setMonitoramentoAtivo] = useState(false);
  const [intervaloMonitoramento, setIntervaloMonitoramento] = useState(30);
  const [importarAutomaticamente, setImportarAutomaticamente] = useState(false);
  const [tempoRestante, setTempoRestante] = useState(0);
  const [verificando, setVerificando] = useState(false);
  const [novosArquivos, setNovosArquivos] = useState([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  
  // Preview
  const [selectedArquivo, setSelectedArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  const empresaId = user?.empresa_id;

  useEffect(() => {
    if (token && empresaId) {
      fetchData();
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [token, empresaId]);

  // Efeito para monitoramento automático
  useEffect(() => {
    if (monitoramentoAtivo && configuracao?.pasta_origem) {
      iniciarTimerMonitoramento();
    } else {
      pararTimerMonitoramento();
    }
    
    return () => pararTimerMonitoramento();
  }, [monitoramentoAtivo, intervaloMonitoramento, configuracao?.pasta_origem]);

  const iniciarTimerMonitoramento = () => {
    pararTimerMonitoramento();
    
    const intervaloMs = intervaloMonitoramento * 60 * 1000;
    setTempoRestante(intervaloMonitoramento * 60);
    
    timerRef.current = setInterval(() => {
      verificarNovosArquivos();
    }, intervaloMs);
    
    countdownRef.current = setInterval(() => {
      setTempoRestante(prev => {
        if (prev <= 1) {
          return intervaloMonitoramento * 60;
        }
        return prev - 1;
      });
    }, 1000);
    
    verificarNovosArquivos();
  };

  const pararTimerMonitoramento = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setTempoRestante(0);
  };

  const verificarNovosArquivos = async () => {
    if (verificando) return;
    setVerificando(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/afd/monitoramento/verificar?empresa_id=${empresaId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setNovosArquivos(data.novos_arquivos || []);
        
        if (data.novos_arquivos?.length > 0) {
          if (data.importados_automaticamente?.length > 0) {
            toast.success(`${data.importados_automaticamente.length} arquivo(s) importado(s) automaticamente`);
            fetchData();
          } else {
            toast.info(`${data.novos_arquivos.length} novo(s) arquivo(s) encontrado(s)`);
          }
        }
      }
    } catch (error) {
      console.error('Erro na verificação:', error);
    } finally {
      setVerificando(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchConfiguracao(),
        fetchArquivosImportados(),
        fetchHistorico()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfiguracao = async () => {
    try {
      const res = await fetch(`${API_URL}/api/afd/configuracao?empresa_id=${empresaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const config = await res.json();
        setConfiguracao(config);
        setPastaServidor(config?.pasta_origem || '');
        setMonitoramentoAtivo(config?.monitoramento_ativo || false);
        setIntervaloMonitoramento(config?.intervalo_monitoramento || 30);
        setImportarAutomaticamente(config?.importar_automaticamente || false);
        
        if (config?.pasta_origem) {
          fetchArquivosServidor();
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const fetchArquivosServidor = async () => {
    if (!configuracao?.pasta_origem) return;
    
    setLoadingArquivosServidor(true);
    try {
      const res = await fetch(`${API_URL}/api/afd/arquivos?empresa_id=${empresaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setArquivosServidor(data.arquivos || []);
      }
    } catch (error) {
      console.error('Erro ao listar arquivos do servidor:', error);
    } finally {
      setLoadingArquivosServidor(false);
    }
  };

  const fetchArquivosImportados = async () => {
    try {
      const res = await fetch(`${API_URL}/api/afd/importacoes?empresa_id=${empresaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setArquivosImportados(data.importacoes || []);
      }
    } catch (error) {
      console.error('Erro ao listar importações:', error);
    }
  };

  const fetchHistorico = async () => {
    try {
      const res = await fetch(`${API_URL}/api/afd/historico?empresa_id=${empresaId}&limite=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHistorico(data.historico || data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    }
  };

  // ==================== CONFIGURAÇÃO DE PASTA NO SERVIDOR ====================

  const handleSalvarConfiguracao = async () => {
    try {
      const res = await fetch(`${API_URL}/api/afd/configuracao`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          pasta_origem: pastaServidor,
          monitoramento_ativo: monitoramentoAtivo,
          intervalo_monitoramento: intervaloMonitoramento,
          importar_automaticamente: importarAutomaticamente
        })
      });
      
      if (res.ok) {
        toast.success('Configuração salva com sucesso!');
        setShowConfigDialog(false);
        fetchConfiguracao();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar configuração');
      }
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const toggleMonitoramento = async () => {
    const novoStatus = !monitoramentoAtivo;
    setMonitoramentoAtivo(novoStatus);
    
    try {
      const endpoint = novoStatus ? 'iniciar' : 'parar';
      await fetch(`${API_URL}/api/afd/monitoramento/${endpoint}?empresa_id=${empresaId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(novoStatus ? 'Monitoramento iniciado' : 'Monitoramento pausado');
    } catch (error) {
      toast.error('Erro ao alterar monitoramento');
      setMonitoramentoAtivo(!novoStatus);
    }
  };

  // ==================== NAVEGADOR DE DIRETÓRIOS DO SERVIDOR ====================

  const abrirExplorer = async (path = '') => {
    setExplorerOpen(true);
    setLoadingExplorer(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/afd/navegar-diretorios?path=${encodeURIComponent(path)}&empresa_id=${empresaId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.ok) {
        const data = await res.json();
        setExplorerPath(data.path || '');
        setExplorerItems(data.items || []);
        setExplorerParent(data.parent || '');
      } else {
        toast.error('Erro ao acessar diretório');
      }
    } catch (error) {
      toast.error('Erro ao explorar diretório');
    } finally {
      setLoadingExplorer(false);
    }
  };

  const selecionarPastaServidor = () => {
    setPastaServidor(explorerPath);
    setExplorerOpen(false);
  };

  // ==================== UPLOAD DE ARQUIVOS LOCAIS ====================
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    adicionarArquivos(files);
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const arquivosAFD = files.filter(f => 
      f.name.toLowerCase().includes('afd') || 
      f.name.toLowerCase().endsWith('.txt') ||
      !f.name.includes('.')
    );
    adicionarArquivos(arquivosAFD);
  };

  const adicionarArquivos = (files) => {
    const novosArquivos = files.filter(f => 
      !arquivosSelecionados.some(a => a.name === f.name && a.size === f.size)
    );
    
    if (novosArquivos.length > 0) {
      setArquivosSelecionados(prev => [...prev, ...novosArquivos]);
      toast.success(`${novosArquivos.length} arquivo(s) adicionado(s)`);
    } else if (files.length > 0) {
      toast.info('Arquivos já estavam na lista');
    }
  };

  const removerArquivo = (index) => {
    setArquivosSelecionados(prev => prev.filter((_, i) => i !== index));
  };

  const limparArquivos = () => {
    setArquivosSelecionados([]);
  };

  // ==================== DRAG AND DROP ====================
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    adicionarArquivos(files);
  };

  // ==================== SELEÇÃO DE PASTA LOCAL (File System Access API) ====================
  
  const selecionarPastaLocal = async () => {
    try {
      if ('showDirectoryPicker' in window) {
        const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
        toast.info(`Lendo pasta: ${dirHandle.name}...`);
        
        const files = await lerArquivosDaPasta(dirHandle);
        
        if (files.length > 0) {
          adicionarArquivos(files);
        } else {
          toast.warning('Nenhum arquivo AFD encontrado na pasta');
        }
      } else {
        folderInputRef.current?.click();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erro ao selecionar pasta:', error);
        toast.error('Erro ao acessar a pasta. Tente selecionar os arquivos individualmente.');
      }
    }
  };

  const lerArquivosDaPasta = async (dirHandle, files = []) => {
    for await (const entry of dirHandle.values()) {
      if (entry.kind === 'file') {
        const file = await entry.getFile();
        if (file.name.toLowerCase().includes('afd') || 
            file.name.toLowerCase().endsWith('.txt') ||
            !file.name.includes('.')) {
          files.push(file);
        }
      } else if (entry.kind === 'directory') {
        await lerArquivosDaPasta(entry, files);
      }
    }
    return files;
  };

  // ==================== IMPORTAÇÃO ====================
  
  const handleImportarLocais = async () => {
    if (arquivosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    let sucessos = 0;
    let erros = 0;
    
    try {
      for (let i = 0; i < arquivosSelecionados.length; i++) {
        const arquivo = arquivosSelecionados[i];
        
        try {
          const conteudo = await arquivo.text();
          
          const res = await fetch(`${API_URL}/api/afd/importar-conteudo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              empresa_id: empresaId,
              nome_arquivo: arquivo.name,
              conteudo: conteudo
            })
          });
          
          if (res.ok) {
            const data = await res.json();
            sucessos++;
            toast.success(`${arquivo.name}: ${data.marcacoes_importadas || 0} marcações`);
          } else {
            const error = await res.json();
            erros++;
            toast.error(`${arquivo.name}: ${error.detail || 'Erro'}`);
          }
        } catch (err) {
          erros++;
          toast.error(`${arquivo.name}: Erro ao processar`);
        }
        
        setUploadProgress(Math.round(((i + 1) / arquivosSelecionados.length) * 100));
      }
      
      if (sucessos > 0) {
        setArquivosSelecionados([]);
        fetchData();
      }
      
    } catch (error) {
      toast.error('Erro durante a importação');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImportarDoServidor = async (nomeArquivo) => {
    try {
      const res = await fetch(
        `${API_URL}/api/afd/importar/${encodeURIComponent(nomeArquivo)}?empresa_id=${empresaId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.marcacoes_importadas || 0} marcações importadas`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao importar');
      }
    } catch (error) {
      toast.error('Erro ao importar arquivo');
    }
  };

  // ==================== PREVIEW ====================
  
  const handlePreview = async (importacao) => {
    setSelectedArquivo(importacao);
    setLoadingPreview(true);
    setShowPreviewDialog(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/afd/preview/${importacao.id}?empresa_id=${empresaId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.ok) {
        setPreview(await res.json());
      } else {
        toast.error('Erro ao carregar preview');
        setShowPreviewDialog(false);
      }
    } catch (error) {
      toast.error('Erro ao carregar preview');
      setShowPreviewDialog(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ==================== FORMATAÇÃO ====================
  
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const formatTempoRestante = (segundos) => {
    const min = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${min}:${seg.toString().padStart(2, '0')}`;
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="importar-afd-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Importar AFD</h1>
          <p className="text-slate-500 mt-1">
            Importe arquivos AFD do computador local ou configure monitoramento automático
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowConfigDialog(true)}
            className="gap-2"
          >
            <Settings className="w-4 h-4" />
            Configurar
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Card de Monitoramento Ativo */}
      {configuracao?.pasta_origem && (
        <Card className={`border ${
          monitoramentoAtivo 
            ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' 
            : 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${monitoramentoAtivo ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  {monitoramentoAtivo ? (
                    <MonitorCheck className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <Pause className="w-6 h-6 text-slate-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">
                    Monitoramento {monitoramentoAtivo ? 'Ativo' : 'Pausado'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    Pasta: {configuracao.pasta_origem}
                  </p>
                  {monitoramentoAtivo && (
                    <p className="text-sm text-emerald-600">
                      Verificando a cada {intervaloMonitoramento} minutos
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {monitoramentoAtivo && tempoRestante > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Próxima verificação</p>
                    <p className="text-2xl font-mono font-bold text-emerald-600">
                      {formatTempoRestante(tempoRestante)}
                    </p>
                  </div>
                )}
                
                <Button
                  variant={monitoramentoAtivo ? "destructive" : "default"}
                  className={!monitoramentoAtivo ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  onClick={toggleMonitoramento}
                  disabled={verificando}
                >
                  {verificando ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : monitoramentoAtivo ? (
                    <Pause className="w-4 h-4 mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {monitoramentoAtivo ? 'Pausar' : 'Iniciar'}
                </Button>
              </div>
            </div>
            
            {/* Novos arquivos detectados */}
            {novosArquivos.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800">
                  {novosArquivos.length} novo(s) arquivo(s) detectado(s)
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {novosArquivos.slice(0, 5).map((arq, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white">
                      {arq}
                    </Badge>
                  ))}
                  {novosArquivos.length > 5 && (
                    <Badge variant="outline">+{novosArquivos.length - 5} mais</Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alerta de configuração necessária */}
      {!configuracao?.pasta_origem && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Configuração de Monitoramento</h3>
                <p className="text-sm text-amber-600">
                  Configure uma pasta no servidor para habilitar o monitoramento automático de arquivos AFD.
                </p>
              </div>
              <Button onClick={() => setShowConfigDialog(true)} className="gap-2">
                <Settings className="w-4 h-4" />
                Configurar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-fit grid-cols-4">
          <TabsTrigger value="importar" className="gap-2">
            <Upload className="w-4 h-4" />
            Upload Local
          </TabsTrigger>
          <TabsTrigger value="servidor" className="gap-2">
            <Server className="w-4 h-4" />
            Pasta Servidor
          </TabsTrigger>
          <TabsTrigger value="importados" className="gap-2">
            <Files className="w-4 h-4" />
            Importados ({arquivosImportados.length})
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <History className="w-4 h-4" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* Aba Upload Local */}
        <TabsContent value="importar" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Área de Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HardDrive className="w-5 h-5" />
                  Upload do Computador
                </CardTitle>
                <CardDescription>
                  Arraste arquivos AFD ou selecione do seu computador
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Zona de Drag and Drop */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
                    dragOver 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="dropzone"
                >
                  <Upload className={`w-12 h-12 mx-auto mb-3 ${dragOver ? 'text-blue-500' : 'text-slate-400'}`} />
                  <p className="text-slate-600 font-medium">
                    {dragOver ? 'Solte os arquivos aqui' : 'Arraste arquivos AFD aqui'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    ou clique para selecionar
                  </p>
                </div>

                {/* Inputs ocultos */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".txt,.afd,*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  {...{ webkitdirectory: '', directory: '' }}
                  multiple
                  onChange={handleFolderSelect}
                  className="hidden"
                />

                {/* Botões de seleção */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FileText className="w-4 h-4" />
                    Arquivos
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={selecionarPastaLocal}
                    data-testid="btn-selecionar-pasta"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Pasta Local
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Lista de Arquivos Selecionados */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Files className="w-5 h-5" />
                    Selecionados ({arquivosSelecionados.length})
                  </CardTitle>
                  {arquivosSelecionados.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={limparArquivos}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Limpar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {arquivosSelecionados.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhum arquivo selecionado</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {arquivosSelecionados.map((arquivo, index) => (
                      <div 
                        key={`${arquivo.name}-${index}`}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-slate-700 text-sm">{arquivo.name}</p>
                            <p className="text-xs text-slate-400">{formatBytes(arquivo.size)}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removerArquivo(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div className="mt-4 space-y-2">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-sm text-center text-slate-500">
                      Importando... {uploadProgress}%
                    </p>
                  </div>
                )}

                <Button
                  className="w-full mt-4 gap-2"
                  disabled={arquivosSelecionados.length === 0 || uploading}
                  onClick={handleImportarLocais}
                  data-testid="btn-importar"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importar {arquivosSelecionados.length > 0 && `(${arquivosSelecionados.length})`}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba Pasta Servidor */}
        <TabsContent value="servidor" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    Arquivos na Pasta do Servidor
                  </CardTitle>
                  <CardDescription>
                    {configuracao?.pasta_origem || 'Nenhuma pasta configurada'}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchArquivosServidor}
                  disabled={!configuracao?.pasta_origem || loadingArquivosServidor}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingArquivosServidor ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!configuracao?.pasta_origem ? (
                <div className="text-center py-12 text-slate-400">
                  <Folder className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Configure uma pasta nas configurações</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowConfigDialog(true)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configurar Pasta
                  </Button>
                </div>
              ) : loadingArquivosServidor ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : arquivosServidor.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum arquivo AFD encontrado na pasta</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Tamanho</TableHead>
                      <TableHead>Modificado</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {arquivosServidor.map((file, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {file.nome}
                          </div>
                        </TableCell>
                        <TableCell>{file.tamanho}</TableCell>
                        <TableCell>{file.data_modificacao}</TableCell>
                        <TableCell>
                          {file.importado ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" />
                              Importado
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pendente</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!file.importado && (
                            <Button 
                              size="sm"
                              onClick={() => handleImportarDoServidor(file.nome)}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Importar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Importados */}
        <TabsContent value="importados" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Arquivos Importados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {arquivosImportados.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Files className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum arquivo importado ainda</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Marcações</TableHead>
                      <TableHead>Empregados</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {arquivosImportados.map((imp) => (
                      <TableRow key={imp.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {imp.nome_arquivo}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(imp.data_importacao)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{imp.total_marcacoes || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{imp.total_empregados || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          {imp.status === 'sucesso' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Sucesso</Badge>
                          ) : imp.status === 'parcial' ? (
                            <Badge className="bg-amber-100 text-amber-700">Parcial</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Erro</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(imp)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Histórico */}
        <TabsContent value="historico" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Histórico de Importações
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historico.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhuma importação registrada</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Registros</TableHead>
                      <TableHead>Resultado</TableHead>
                      <TableHead>Usuário</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historico.map((h, idx) => (
                      <TableRow key={h.id || idx}>
                        <TableCell className="text-sm">
                          {formatDate(h.data_importacao || h.created_at || h.iniciado_em)}
                        </TableCell>
                        <TableCell className="font-medium">{h.nome_arquivo}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-emerald-600">{h.registros_sucesso || h.marcacoes_importadas || 0} ✓</span>
                            {(h.registros_erro || 0) > 0 && (
                              <span className="text-red-600 ml-2">{h.registros_erro} ✗</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(h.status === 'sucesso' || h.status === 'concluida') ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Sucesso</Badge>
                          ) : h.status === 'parcial' ? (
                            <Badge className="bg-amber-100 text-amber-700">Parcial</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Erro</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {h.usuario_nome || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de Configuração */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configuração de Monitoramento AFD</DialogTitle>
            <DialogDescription>
              Configure a pasta do servidor e o intervalo de verificação automática
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Pasta do Servidor */}
            <div className="space-y-2">
              <Label>Pasta no Servidor</Label>
              <div className="flex gap-2">
                <Input
                  value={pastaServidor}
                  onChange={(e) => setPastaServidor(e.target.value)}
                  placeholder="/caminho/para/arquivos/afd"
                  className="flex-1"
                />
                <Button variant="outline" onClick={() => abrirExplorer(pastaServidor || '')}>
                  <FolderOpen className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Caminho da pasta no servidor onde os arquivos AFD são salvos
              </p>
            </div>

            {/* Intervalo de Monitoramento */}
            <div className="space-y-2">
              <Label>Intervalo de Verificação</Label>
              <Select 
                value={intervaloMonitoramento.toString()} 
                onValueChange={(v) => setIntervaloMonitoramento(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">A cada 5 minutos</SelectItem>
                  <SelectItem value="10">A cada 10 minutos</SelectItem>
                  <SelectItem value="15">A cada 15 minutos</SelectItem>
                  <SelectItem value="30">A cada 30 minutos</SelectItem>
                  <SelectItem value="60">A cada 1 hora</SelectItem>
                  <SelectItem value="120">A cada 2 horas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Importação Automática */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Importar Automaticamente</Label>
                <p className="text-xs text-slate-500">
                  Importar novos arquivos automaticamente quando detectados
                </p>
              </div>
              <Switch
                checked={importarAutomaticamente}
                onCheckedChange={setImportarAutomaticamente}
              />
            </div>

            {/* Ativar Monitoramento */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base">Ativar Monitoramento</Label>
                <p className="text-xs text-slate-500">
                  Iniciar verificação automática da pasta
                </p>
              </div>
              <Switch
                checked={monitoramentoAtivo}
                onCheckedChange={setMonitoramentoAtivo}
                disabled={!pastaServidor}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvarConfiguracao}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Explorer de Diretórios */}
      <Dialog open={explorerOpen} onOpenChange={setExplorerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Selecionar Pasta</DialogTitle>
            <DialogDescription>
              Navegue pelos diretórios do servidor e selecione a pasta de arquivos AFD
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Caminho atual */}
            <div className="flex items-center gap-2 p-2 bg-slate-100 rounded">
              <Folder className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-mono">{explorerPath || '/'}</span>
            </div>

            {/* Navegação */}
            <div className="flex gap-2">
              {explorerParent !== null && (
                <Button variant="outline" size="sm" onClick={() => abrirExplorer(explorerParent)}>
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Voltar
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => abrirExplorer('')}>
                <Home className="w-4 h-4 mr-1" />
                Início
              </Button>
            </div>

            {/* Lista de itens */}
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {loadingExplorer ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : explorerItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>Pasta vazia</p>
                </div>
              ) : (
                <div className="divide-y">
                  {explorerItems.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer ${
                        item.type === 'directory' ? '' : 'opacity-50'
                      }`}
                      onClick={() => item.type === 'directory' && abrirExplorer(item.path)}
                    >
                      {item.type === 'directory' ? (
                        <Folder className="w-5 h-5 text-amber-500" />
                      ) : (
                        <FileText className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="flex-1">{item.name}</span>
                      {item.type === 'directory' && (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExplorerOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={selecionarPastaServidor}>
              Selecionar Esta Pasta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Preview */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview - {selectedArquivo?.nome_arquivo}</DialogTitle>
          </DialogHeader>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-blue-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{preview.total_registros || 0}</p>
                    <p className="text-sm text-blue-500">Registros</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{preview.total_empregados || 0}</p>
                    <p className="text-sm text-emerald-500">Empregados</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{preview.periodo || '-'}</p>
                    <p className="text-sm text-purple-500">Período</p>
                  </CardContent>
                </Card>
              </div>
              
              {preview.amostra && preview.amostra.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Amostra de Registros</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>NSR</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Hora</TableHead>
                        <TableHead>CPF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.amostra.slice(0, 10).map((reg, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{reg.nsr}</TableCell>
                          <TableCell>{reg.data}</TableCell>
                          <TableCell>{reg.hora}</TableCell>
                          <TableCell>{reg.cpf}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-8">Sem dados para exibir</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
